import { XMarkIcon } from '@heroicons/react/24/outline'
import { useForm } from 'libs/form'
import { useTranslation } from 'libs/i18n'
import { Button, Dialog } from 'shared/components'
import { InputGroup } from 'shared/molecules'
import { useNameValidationSchema } from './hooks/useNameValidationSchema'

type NameFormValues = {
	name: string
}

type NameDialogProps = {
	title: string
	submitText: string
	defaultValue?: string
	isLoading: boolean
	closeModal: () => void
	onSubmit: (values: NameFormValues) => void
}

const cancelButtonClassName =
	'h-9 rounded-md border-0 px-4 text-sm font-medium text-foreground shadow-none hover:bg-muted'
const submitButtonClassName =
	'h-9 rounded-md px-4 text-sm font-medium shadow transition-colors hover:bg-primary/90'

export const NameDialog = ({
	title,
	submitText,
	defaultValue = '',
	isLoading,
	closeModal,
	onSubmit
}: NameDialogProps) => {
	const { t } = useTranslation()
	const validationResolver = useNameValidationSchema()
	const {
		register,
		setFocus,
		handleSubmit,
		formState: { errors }
	} = useForm<NameFormValues>({
		mode: 'onChange',
		resolver: validationResolver,
		defaultValues: { name: defaultValue }
	})

	return (
		<Dialog
			isOpened
			ariaLabel={title}
			className="max-w-md"
			closeModal={closeModal}
			onAfterOpen={() => setFocus('name')}>
			<form
				className="flex min-h-0 flex-1 flex-col"
				onSubmit={handleSubmit((values) => onSubmit(values))}>
				<div className="min-h-0 flex-1 overflow-y-auto px-6 pb-5 pt-6">
					<h2 className="pr-8 font-display text-lg font-bold tracking-tight text-foreground">
						{title}
					</h2>
					<InputGroup
						id="name"
						className="mt-4"
						label={t('Name')}
						required
						{...register('name')}
						error={errors.name}
					/>
				</div>
				<button
					type="button"
					aria-label={t('Close')}
					className="absolute right-4 top-4 grid h-8 w-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
					onClick={closeModal}>
					<XMarkIcon className="h-4 w-4 stroke-2" />
				</button>
				<div className="flex items-center justify-end gap-2 border-t border-border bg-background/60 px-6 py-4">
					<Button styleType="outline" className={cancelButtonClassName} onClick={closeModal}>
						{t('Cancel')}
					</Button>
					<Button type="submit" className={submitButtonClassName} isLoading={isLoading}>
						{submitText}
					</Button>
				</div>
			</form>
		</Dialog>
	)
}
